import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import { useGetStudentByIdQuery } from "../api/studentApi";
import { API_CONFIG } from "../../../../core/config";
import { useStudent } from "../hooks/useStudent";
import { showToast } from "../../../../core/utils/alert";

// Component for uploading student documents
const StudentDocuments = () => {
  const routes = all_routes;
  const { id } = useParams<{ id: string }>();
  const { data: studentResponse, isLoading } = useGetStudentByIdQuery({
    id: id || "",
  });
  const student = studentResponse?.data?.student;
  const { uploadStudentDobDocument, uploadStudentClassProofDocument } = useStudent();

  const [documentType, setDocumentType] = useState<"dob" | "class_proof">("dob");
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const navigate = useNavigate();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFileError(null);
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (selectedFile.type !== "application/pdf") {
        setFileError("Please upload a valid PDF file.");
        e.target.value = "";
        setFile(null);
        return;
      }
      
      const maxSize = 2 * 1024 * 1024; // 2MB
      if (selectedFile.size > maxSize) {
        setFileError("File size must not exceed 2MB.");
        e.target.value = "";
        setFile(null);
        return;
      }
      
      setFile(selectedFile);
    }
  };

  const handleDocumentTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDocumentType(e.target.value as "dob" | "class_proof");
    setFileError(null);
    setFile(null); // Reset file on type change
  };

  const handleUpload = async () => {
    if (!id || !file) return;

    setIsUploading(true);
    try {
      let uploadResponse;
      if (documentType === "dob") {
        uploadResponse = await uploadStudentDobDocument(
          id,
          file,
          student?.status
        );
      } else {
        uploadResponse = await uploadStudentClassProofDocument(id, file);
      }

      if (!uploadResponse.success) {
        showToast({
          title: "Error",
          text: uploadResponse.error || "Failed to upload document",
          icon: "error",
        });
        return;
      }
      showToast({
        title: "Success",
        text: "Document uploaded successfully",
        icon: "success",
      });
      navigate(`${routes.studentList}?refresh=${Date.now()}`);
    } catch (error) {
      showToast({
        title: "Error",
        text: error instanceof Error ? error.message : "Document upload failed",
        icon: "error",
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">Upload Student Documents</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.studentList}>Students</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Upload Student Documents
                </li>
              </ol>
            </nav>
          </div>
        </div>

        <div className="row justify-content-center">
          <div className="col-xl-8 col-md-10">
            <div className="card shadow-sm">
              <div className="card-body p-4">
                <div className="mb-4">
                  <h5 className="card-title">Required Documents</h5>
                  <p className="text-muted">Upload student's documents</p>
                </div>

                {isLoading ? (
                  <div className="text-center">Loading...</div>
                ) : (
                  <>
                    <div className="mb-4">
                      <label className="form-label d-block fw-semibold">
                        Document Type
                      </label>
                      <div className="d-flex gap-4">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="documentType"
                            id="docDob"
                            value="dob"
                            checked={documentType === "dob"}
                            onChange={handleDocumentTypeChange}
                            disabled={isUploading}
                          />
                          <label className="form-check-label" htmlFor="docDob">
                            Date of Birth Proof
                          </label>
                        </div>
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="documentType"
                            id="docClassProof"
                            value="class_proof"
                            checked={documentType === "class_proof"}
                            onChange={handleDocumentTypeChange}
                            disabled={isUploading}
                          />
                          <label className="form-check-label" htmlFor="docClassProof">
                            Class Proof
                          </label>
                        </div>
                      </div>
                    </div>

                    <div className="mb-4">
                      <label className="form-label d-block fw-semibold">
                        {documentType === "dob" ? "Date of Birth Proof (PDF)" : "Class Proof (PDF)"}
                      </label>
                      <div className="custom-file-container border rounded p-3">
                        <input
                          type="file"
                          className={`form-control ${fileError ? 'is-invalid' : ''}`}
                          accept=".pdf"
                          onChange={handleFileChange}
                          disabled={isUploading}
                        />
                        {fileError && <div className="invalid-feedback d-block mt-1">{fileError}</div>}
                        {(file || (documentType === "dob" ? student?.docs?.dob : student?.docs?.class_proof)) && (
                          <div className="mt-2">
                            <a
                              href={
                                file
                                  ? URL.createObjectURL(file)
                                  : (documentType === "dob"
                                    ? `${API_CONFIG.BASE_URL}students/download-dob/${student?.docs?.dob}`
                                    : `${API_CONFIG.BASE_URL}students/download-class-proof/${student?.docs?.class_proof}`
                                  )
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary text-decoration-none"
                            >
                              <i className="ti ti-file-text me-2"></i>
                              {file ? file.name : "View Document"}
                            </a>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="d-flex justify-content-end gap-3 mt-5">
                      <Link to={routes.studentList} className="btn btn-light">
                        Cancel
                      </Link>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleUpload}
                        disabled={isUploading || !file}
                      >
                        {isUploading ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                            ></span>
                            Uploading...
                          </>
                        ) : (
                          "Upload Document"
                        )}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentDocuments;
